import { InspectionBreakdown, MixingWork } from '@twinsketch/topika-model';
import React, { useState } from 'react';

interface ViscosityChartProps {
  mixingWorks: MixingWork[];
  inspectionBreakdown: InspectionBreakdown;
}

const FinalViscosityChart: React.FC<ViscosityChartProps> = ({
  mixingWorks,
  inspectionBreakdown,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);

  const {
    slurryViscosityMin,
    slurryViscosityMax,
    slurryViscosityUnit = 'cps',
  } = inspectionBreakdown;

  // Guard clause - if no spec range defined, show message
  if (!slurryViscosityMin || !slurryViscosityMax) {
    return (
      <div
        style={{
          width: '100%',
          maxWidth: '48rem',
          margin: '0 auto',
          padding: '1.5rem',
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          border: '1px solid #e5e7eb',
        }}
      >
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
          Final Viscosity Comparison
        </h3>
        <p style={{ color: '#6b7280', fontStyle: 'italic' }}>
          No viscosity specification range defined
        </p>
      </div>
    );
  }

  // Filter out mixing works without viscosity data
  const validMixingWorks = (mixingWorks || []).filter(
    w => w.measuredViscosityCps != null && w.measuredViscosityCps != null
  );
  if (validMixingWorks.length === 0) {
    return (
      <div
        style={{
          width: '100%',
          maxWidth: '48rem',
          margin: '0 auto',
          padding: '1.5rem',
          backgroundColor: 'white',
          borderRadius: '0.5rem',
          border: '1px solid #e5e7eb',
        }}
      >
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
          Final Viscosity Comparison
        </h3>
        <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No viscosity measurements available</p>
      </div>
    );
  }

  // Dynamic label configuration based on data density
  const dataCount = validMixingWorks.length;
  const shouldRotate = dataCount > 5;

  // Calculate chart dimensions and scale
  const maxViscosity = Math.max(
    ...validMixingWorks.map(w => w.measuredViscosityCps),
    slurryViscosityMax
  );
  const chartMax = Math.ceil(maxViscosity / 1000) * 1000 + 2000; // Add padding
  const chartHeight = 320;

  // More compact width calculation for closer bars
  const minChartWidth = 400;
  const barWidthIdeal = 15;
  const gapBetweenBars = dataCount > 15 ? 4 : dataCount > 10 ? 6 : 8;
  const totalBarsWidth = dataCount * (barWidthIdeal + gapBetweenBars);
  const chartWidth = Math.max(minChartWidth, totalBarsWidth + 120);

  // Dynamic bottom padding based on rotation
  const bottomPadding = shouldRotate ? 100 : 60;
  const padding = {
    top: 40,
    right: 140,
    bottom: bottomPadding,
    left: 80,
  };

  const plotHeight = chartHeight - padding.top - padding.bottom;
  const plotWidth = chartWidth - padding.left - padding.right;

  // Scale function
  const scaleY = (value: number) => {
    return plotHeight - (value / chartMax) * plotHeight;
  };

  // Bar width and spacing - closer together
  const actualBarWidth = Math.min(
    barWidthIdeal,
    (plotWidth - (dataCount - 1) * gapBetweenBars) / dataCount
  );
  const barWithGap = actualBarWidth + gapBetweenBars;

  // Y-axis ticks - dynamically generate based on chartMax
  const tickInterval = chartMax <= 5000 ? 1000 : 2000;
  const yTicks: number[] = [];
  for (let i = 0; i <= chartMax; i += tickInterval) {
    yTicks.push(i);
  }

  const handleMouseEnter = (index: number, event: React.MouseEvent<SVGRectElement>) => {
    setHoveredIndex(index);
    const rect = event.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top,
    });
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
    setTooltipPosition(null);
  };

  return (
    <div className="chart-card" style={{ position: 'relative' }}>
      <div>
        <h3>Final Viscosity Comparison</h3>
        <span className="chart-helper-text">Hover over bars for details</span>
      </div>
      <div className="chart-container">
        <svg width={chartWidth} height={chartHeight}>
          {/* Legend at top right - positioned above the plot area */}
          <g>
            {/* Legend background */}
            <rect
              x={chartWidth - padding.right + 10}
              y={5}
              width={120}
              height={50}
              fill="white"
              stroke="#e5e7eb"
              strokeWidth="1"
              rx="4"
              opacity="0.95"
            />

            {/* Within Spec */}
            <rect
              x={chartWidth - padding.right + 18}
              y={13}
              width={12}
              height={12}
              fill="#10b981"
              opacity="0.8"
              rx="2"
            />
            <text
              x={chartWidth - padding.right + 36}
              y={19}
              fontSize="11"
              fill="#374151"
              dominantBaseline="middle"
            >
              Within Spec
            </text>

            {/* Out of Spec */}
            <rect
              x={chartWidth - padding.right + 18}
              y={33}
              width={12}
              height={12}
              fill="#ef4444"
              opacity="0.8"
              rx="2"
            />
            <text
              x={chartWidth - padding.right + 36}
              y={39}
              fontSize="11"
              fill="#374151"
              dominantBaseline="middle"
            >
              Out of Spec
            </text>
          </g>

          {/* Target Range Band */}
          <rect
            x={padding.left}
            y={scaleY(slurryViscosityMax) + padding.top}
            width={plotWidth}
            height={scaleY(slurryViscosityMin) - scaleY(slurryViscosityMax)}
            fill="#fef3c7"
            stroke="#fbbf24"
            strokeWidth="2"
            strokeDasharray="4,4"
            opacity="0.5"
          />

          {/* Target Range Label */}
          <text
            x={padding.left + plotWidth + 5}
            y={scaleY((slurryViscosityMin + slurryViscosityMax) / 2) + padding.top}
            fontSize="11"
            fill="#92400e"
            dominantBaseline="middle"
          >
            Target Range
          </text>

          {/* Upper limit line and label */}
          <line
            x1={padding.left}
            y1={scaleY(slurryViscosityMax) + padding.top}
            x2={padding.left + plotWidth}
            y2={scaleY(slurryViscosityMax) + padding.top}
            stroke="#fbbf24"
            strokeWidth="2"
          />
          <text
            x={padding.left + plotWidth + 5}
            y={scaleY(slurryViscosityMax) + padding.top - 5}
            fontSize="10"
            fill="#92400e"
          >
            Upper ({slurryViscosityMax.toLocaleString()})
          </text>

          {/* Lower limit line and label */}
          <line
            x1={padding.left}
            y1={scaleY(slurryViscosityMin) + padding.top}
            x2={padding.left + plotWidth}
            y2={scaleY(slurryViscosityMin) + padding.top}
            stroke="#fbbf24"
            strokeWidth="2"
          />
          <text
            x={padding.left + plotWidth + 5}
            y={scaleY(slurryViscosityMin) + padding.top + 5}
            fontSize="10"
            fill="#92400e"
          >
            Lower ({slurryViscosityMin.toLocaleString()})
          </text>

          {/* Y-axis */}
          <line
            x1={padding.left}
            y1={padding.top}
            x2={padding.left}
            y2={chartHeight - padding.bottom}
            stroke="#374151"
            strokeWidth="2"
          />

          {/* Y-axis ticks and labels */}
          {yTicks.map(tick => (
            <g key={tick}>
              <line
                x1={padding.left - 5}
                y1={scaleY(tick) + padding.top}
                x2={padding.left}
                y2={scaleY(tick) + padding.top}
                stroke="#374151"
                strokeWidth="2"
              />
              <text
                x={padding.left - 10}
                y={scaleY(tick) + padding.top}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize="12"
                fill="#374151"
              >
                {tick.toLocaleString()}
              </text>
            </g>
          ))}

          {/* Y-axis label */}
          <text
            x={20}
            y={padding.top + plotHeight / 2}
            textAnchor="middle"
            fontSize="14"
            fill="#374151"
            transform={`rotate(-90, 20, ${padding.top + plotHeight / 2})`}
          >
            {slurryViscosityUnit}
          </text>

          {/* X-axis */}
          <line
            x1={padding.left}
            y1={chartHeight - padding.bottom}
            x2={chartWidth - padding.right}
            y2={chartHeight - padding.bottom}
            stroke="#374151"
            strokeWidth="2"
          />

          {/* Bars and labels */}
          {validMixingWorks.map((work, index) => {
            const xPosition = padding.left + index * barWithGap;
            const barHeight = plotHeight - scaleY(work.measuredViscosityCps);
            const yPosition = scaleY(work.measuredViscosityCps) + padding.top;

            const isInSpec =
              work.measuredViscosityCps >= slurryViscosityMin &&
              work.measuredViscosityCps <= slurryViscosityMax;

            // Tooltip dimensions
            const tooltipWidth = 180;
            const tooltipHeight = 90;

            // Calculate tooltip position (center it on the bar, adjust if near edges)
            let tooltipX = xPosition + actualBarWidth / 2 - tooltipWidth / 2;
            if (tooltipX < padding.left) {
              tooltipX = padding.left + 5;
            } else if (tooltipX + tooltipWidth > chartWidth - padding.right) {
              tooltipX = chartWidth - padding.right - tooltipWidth - 5;
            }

            // Smart tooltip Y positioning - flip to below if would be cut off at top
            let tooltipY = yPosition - tooltipHeight - 15;
            if (tooltipY < 0) {
              // Flip to below the bar if it would be cut off at top
              tooltipY = yPosition + barHeight + 15;
            }
            return (
              <g key={work.lotNumber}>
                {/* Bar with hover */}
                <rect
                  x={xPosition}
                  y={yPosition}
                  width={actualBarWidth}
                  height={barHeight}
                  fill={isInSpec ? '#10b981' : '#ef4444'}
                  opacity={hoveredIndex === index ? 1 : 0.8}
                  style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                  onMouseEnter={e => handleMouseEnter(index, e)}
                  onMouseLeave={handleMouseLeave}
                />
              </g>
            );
          })}
        </svg>
        {/* Tooltip on hover */}
        {/* HTML Tooltip - rendered outside SVG */}
        {hoveredIndex !== null && tooltipPosition && (
          <div
            style={{
              position: 'fixed',
              left: tooltipPosition.x,
              top: tooltipPosition.y - 100,
              transform: 'translateX(-50%)',
              width: '180px',
              padding: '12px',
              backgroundColor: 'white',
              border: '1.5px solid #d1d5db',
              borderRadius: '6px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              pointerEvents: 'none',
              zIndex: 1000,
            }}
          >
            {(() => {
              const work = validMixingWorks[hoveredIndex];
              const isInSpec =
                work.measuredViscosityCps >= slurryViscosityMin &&
                work.measuredViscosityCps <= slurryViscosityMax;

              return (
                <>
                  <div
                    style={{
                      fontWeight: '700',
                      fontSize: '12px',
                      color: '#374151',
                      marginBottom: '8px',
                    }}
                  >
                    {work.lotNumber}
                  </div>
                  <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '8px' }}>
                    Date:{' '}
                    {new Date(work.date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </div>
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: '700',
                      color: isInSpec ? '#059669' : '#dc2626',
                      marginBottom: '6px',
                    }}
                  >
                    {work.measuredViscosityCps.toLocaleString()} {slurryViscosityUnit}
                  </div>
                  <div
                    style={{
                      fontSize: '10px',
                      fontWeight: '600',
                      color: isInSpec ? '#059669' : '#dc2626',
                    }}
                  >
                    {isInSpec ? '✓ Within Spec' : '⚠ Out of Spec'}
                  </div>
                </>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
};

export default FinalViscosityChart;
